import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
} from '@fluentui/react-components';
import { ArrowClockwise20Regular, ArrowLeft20Regular, ArrowUndo20Regular, Delete20Regular, Eye20Regular, Link20Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';

import { GuideDrawer, GuideDrawerAction, GuideDrawerSection } from '../../../theme/components/GuideDrawer';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';
import { GuideTable, GuideTh, guideCellClassName as cell, guideRowClassName } from '../../../theme/components/GuideTable';
import { GuideEmptyState } from '../../../theme/components/GuideEmptyState';
import { GuideSkeleton } from '../../../theme/components/GuideSkeleton';
import { GuideKpiCard, GuideKpiGrid } from '../../../theme/components/GuideKpiCard';
import { GuideBannerAction } from '../../../theme/components/GuideButtons';
import { GuideStepper } from '../../../theme/components/GuideStepper';
import { StatusPill } from '../../../theme/components/StatusPill';
import { GuideTooltip } from '../../../theme/components/GuideTooltip';
import { ViewInFilesLink } from '../../Files/ViewInFilesLink/ViewInFilesLink.Component';
import FilePreview from '../../Files/FilePreview/FilePreview.Component';
import FilePickerDialog from '../../Files/FilePickerDialog/FilePickerDialog.Component';
import { getOwnerFolderPath } from '../../../functions/StorageBrowser.Functions';
import { PreviewFile } from '../../Files/FilePreview/FilePreview.Functions';
import { ImportMappingPreview } from './ImportMappingPreview';
import { ImportItemsReview } from './ImportItemsReview';
import { useImportSelection } from './useImportSelection';
import { useAssignmentOptions } from './useAssignmentOptions';
import { ImportJobProgressBar } from './ImportJobProgress';
import { useSyncPolling } from '../ProviderConsumptions/useSyncPolling';
import {
  browserNotificationPermission,
  BrowserNotificationPermission,
  conflictJobIdOf,
  getBrowserNotifyPreference,
  IMPORT_JOB_DRAWER_POLL_MS,
  importJobErrorText,
  importJobResultOf,
  isImportJob,
  isImportJobActive,
  isImportJobFailed,
  isImportJobFinished,
  isImportJobSucceeded,
  placeholderImportJob,
  requestBrowserNotificationPermission,
  setBrowserNotifyPreference,
} from '../../../functions/ImportJobs.Functions';
import { formatDateTime } from '../../../functions/ProviderConsumptions.Functions';
import {
  buildReviewImportRequest,
  conflictReasonOf,
  excludedByStatusDetail,
  excludedByStatusTotal,
  isPreviewGoneError,
} from '../../../functions/ProviderConsumptionImport.Review.Functions';

import {
  buildImportRequest,
  buildMappingOverride,
  fileDisplayName,
  filePreviewPath,
  formatImportDate,
  getApiErrorMessage,
  importableFiles,
  ImportMappingForm,
  isConflictError,
  isDocumentStrategy,
  isValidCurrencyCode,
  mappingFormFrom,
  sameMappingForm,
} from '../../../functions/ProviderConsumptionImport.Functions';
import { formatFileSize, getProviderStorageOwner } from '../../../functions/AzureFileUpload.Functions';
import { ProviderFileViewModel } from '../../../models/Entities/AzureFiles/AzureFileUpload';
import {
  DeleteImportedConsumptionsResult,
  IMPORTABLE_FILE_EXTENSIONS,
  ImportMappingOverride,
  ImportPreview,
  ImportProviderFileConsumptionsRequest,
  ImportProviderFileConsumptionsResult,
  ImportRequestError,
  ImportStartResponse,
  PreviewItemIds,
  PreviewItemsFilter,
  PreviewItemsPage,
  PreviewItemsQuery,
  ProtocolReferenceTemplate,
  ProviderImportJob,
  PROVIDER_FILE_TYPE_INVOICE,
  ReferenceTemplatePart,
  SaveReferenceTemplateRequest,
  TemplateTestResult,
} from '../../../models/Entities/ProviderConsumptionImport/ProviderConsumptionImport';
import { Protocol } from '../../../models/Entities/Protocols/Protocol';
import { ProtocolPatient } from '../../../models/Entities/Protocols/ProtocolPatient';
import { ErrorType } from '../../../models/HttpError';
import './ProviderConsumptionImport.Styles.css';

/** Cantidad máxima de avisos que se listan (el archivo puede tener miles de filas). */
export const MAX_LISTED_WARNINGS = 20;

/** Pasos del asistente (spec 009: "Revisar ítems" sólo cuando la vista previa trae `previewId`). */
export const IMPORT_STEPS = ['file', 'mapping', 'review', 'import'] as const;
export type ImportStep = (typeof IMPORT_STEPS)[number];
/** Pasos de un backend anterior a MedicalProtocols 031 (sin vista previa congelada): los 3 de 008. */
export const LEGACY_IMPORT_STEPS: ImportStep[] = ['file', 'mapping', 'import'];

/** Aviso del paso de revisión: la vista previa venció / se reemplazó, o el mapeo cambió y se descartó la selección. */
type ReviewNotice = 'expired' | 'discarded';

export interface ImportConsumptionsDrawerProps {
  open: boolean;
  onDismiss: () => void;
  providerId: number;
  providerName: string;
  files: ProviderFileViewModel[];
  filesLoading: boolean;
  filesError?: ErrorType;
  onReloadFiles: () => void | Promise<void>;
  /** Vista previa del mapeo (MP 029 §1); no escribe nada. */
  onPreview: (fileId: string, body: ImportProviderFileConsumptionsRequest) => Promise<ImportPreview | undefined>;
  /**
   * Importación con el mismo cuerpo de la última vista previa: 202 + job (MP 030, segundo plano) o el resumen
   * sincrónico de un backend anterior. 409 sin `JobId` = otro mapeo; 409 con `JobId` = ya hay una en curso.
   */
  onImport: (fileId: string, body: ImportProviderFileConsumptionsRequest) => Promise<ImportStartResponse>;
  /**
   * Página de ítems de la vista previa congelada (spec 009, MP 031). Con este callback, `onLoadPreviewItemIds` y una vista
   * previa con `previewId` el asistente muestra el paso "Revisar ítems"; si falta alguno, importa como 008.
   */
  onLoadPreviewItems?: (previewId: string, query: PreviewItemsQuery) => Promise<PreviewItemsPage>;
  /** Ids e importes de los ítems seleccionables que cumplen los filtros (selección inicial y "Todos los filtrados"). */
  onLoadPreviewItemIds?: (previewId: string, filter?: PreviewItemsFilter) => Promise<PreviewItemIds>;
  /** Protocolos para "Asignar a seleccionados" y la asignación por fila (spec 009, US2). Sin él no se ofrece asignar. */
  onLoadProtocols?: () => Promise<Protocol[]>;
  /** Pacientes del protocolo (combobox de paciente). */
  onLoadPatients?: (protocolId: number) => Promise<ProtocolPatient[]>;
  /** Prueba de un formato de referencia (US5). Con éste y `onCreateTemplate` se ofrece "Definir formato a partir de este ítem". */
  onTestTemplate?: (parts: ReferenceTemplatePart[], texts: string[]) => Promise<TemplateTestResult[]>;
  /** Alta de un formato de referencia del proveedor (US5). */
  onCreateTemplate?: (body: SaveReferenceTemplateRequest) => Promise<ProtocolReferenceTemplate | undefined>;
  /** Lee el job (paso 3, cada 3 s mientras está activo). */
  onLoadJob?: (jobId: string) => Promise<ProviderImportJob | undefined>;
  /** Se inició o se siguió (409) un job: lo registra en el aviso global. */
  onJobStarted?: (job: ProviderImportJob) => void;
  /** El asistente abierto muestra el job (`true`) o dejó de mostrarlo (`false`). */
  onJobForeground?: (jobId: string, foreground: boolean) => void;
  /** El fin del job se mostró en el asistente: el aviso global no lo repite. */
  onJobFinished?: (job: ProviderImportJob) => void;
  /** Archivo preseleccionado al abrir ("Reintentar" del aviso global). */
  initialFileId?: string;
  /** La importación terminó bien (para recargar la lista y filtrar por el archivo). */
  onImported?: (fileId: string, result: ImportProviderFileConsumptionsResult | undefined) => void;
  /** Elimina los consumos importados del archivo (MP 029 §3, todo o nada). Sin callback no se ofrece. */
  onDeleteImported?: (fileId: string) => Promise<DeleteImportedConsumptionsResult | undefined>;
  /** Se eliminaron consumos importados (para recargar la grilla). */
  onDeletedImported?: (fileId: string, result: DeleteImportedConsumptionsResult | undefined) => void;
  /** "Deshacer importación" de un archivo de la lista (spec 010, US7): la sección abre su diálogo. Sin callback no se ofrece. */
  onUndoImport?: (file: ProviderFileViewModel) => void;
  /**
   * "Vincular archivo del storage" (spec 005, US6): links a storage file (path relative to the tenant) to the provider
   * as services performed; resolves with the id of the new provider file (to preselect it), rejects with ErrorType.
   */
  onLinkFile?: (path: string) => Promise<string | undefined>;
}

/**
 * "Importar consumos" (spec 007): asistente en un GuideDrawer large.
 * 1. Archivo: factura o servicios realizados del proveedor (xlsx / xls / csv / pdf / docx) o vincular uno del storage.
 * 2. Mapeo: vista previa de la normalización de la IA, mapeo editable y "Recalcular vista previa" con el override.
 * 3. Revisar ítems (spec 009, sólo con `previewId`): elegir qué ítems importar de la vista previa congelada.
 * 4. Importación: importa (con `previewId` + `selectedItemIds`, o con el override como 008) y muestra el resumen.
 */
export const ImportConsumptionsDrawer = (props: ImportConsumptionsDrawerProps) => {
  const { t } = useTranslation();
  const tr = (key: string, options?: Record<string, unknown>) => t(`providerConsumptionImport.importDrawer.${key}`, options);
  const trm = (key: string, options?: Record<string, unknown>) => t(`providerConsumptionImport.mapping.${key}`, options);
  const trj = (key: string, options?: Record<string, unknown>) => t(`providerConsumptionImport.jobs.${key}`, options);

  const trv = (key: string, options?: Record<string, unknown>) => t(`providerConsumptionImport.review.${key}`, options);

  const [step, setStep] = useState<ImportStep>('file');
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);
  const [filePreview, setFilePreview] = useState<PreviewFile | undefined>(undefined);
  const [pickerOpen, setPickerOpen] = useState(false);
  /** Id of the last provider file linked from the picker (preselected after the list reloads) */
  const lastLinkedId = useRef<string | undefined>(undefined);

  // ---- paso 2: vista previa del mapeo ----
  const [preview, setPreview] = useState<ImportPreview | undefined>(undefined);
  const [previewFileId, setPreviewFileId] = useState<string | undefined>(undefined);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<unknown>(undefined);
  /** Plan original de la IA (sin override): contra él se calcula qué cambió el usuario. */
  const [baseline, setBaseline] = useState<ImportMappingForm | undefined>(undefined);
  const [form, setForm] = useState<ImportMappingForm | undefined>(undefined);
  /** Formulario de la última vista previa correcta (la importación usa su override). */
  const [applied, setApplied] = useState<ImportMappingForm | undefined>(undefined);
  const previewRequest = useRef(0);

  // ---- eliminación de importados ----
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<unknown>(undefined);
  const [deletedCount, setDeletedCount] = useState<number | undefined>(undefined);

  // ---- paso 3: importación ----
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<unknown>(undefined);
  const [result, setResult] = useState<ImportProviderFileConsumptionsResult | undefined>(undefined);

  // ---- paso 3: importación en segundo plano (spec 008) ----
  const [job, setJob] = useState<ProviderImportJob | undefined>(undefined);
  /** El job se siguió por un 409 (ya había una importación en curso de este archivo). */
  const [alreadyRunning, setAlreadyRunning] = useState(false);
  const [notifyPermission, setNotifyPermission] = useState<BrowserNotificationPermission>(() => browserNotificationPermission());
  const [notifyWanted, setNotifyWanted] = useState(() => getBrowserNotifyPreference() && browserNotificationPermission() === 'granted');
  const finishedJobId = useRef<string | undefined>(undefined);

  // ---- paso "Revisar ítems" (spec 009) ----
  const [reviewNotice, setReviewNotice] = useState<ReviewNotice | undefined>(undefined);
  /** El usuario ya entró a "Revisar ítems" con la vista previa actual (cambiarla descarta la selección). */
  const reviewStarted = useRef(false);
  /** Regeneraciones por una selección inicial que no coincide con el resumen (como máximo una seguida). */
  const staleRegenerations = useRef(0);
  const previewItems = preview?.items ?? [];
  const reviewAvailable =
    !!preview?.previewId &&
    !!props.onLoadPreviewItems &&
    !!props.onLoadPreviewItemIds &&
    (previewItems.length === 0 || previewItems.some((item) => !!item.id));
  // Antes de la vista previa se asume la revisión si el asistente la puede cargar; una vista previa sin previewId
  // (backend anterior a MP 031) deja los 3 pasos de 008.
  const reviewExpected = preview ? reviewAvailable : !!props.onLoadPreviewItems && !!props.onLoadPreviewItemIds;
  const steps: ImportStep[] = reviewExpected ? [...IMPORT_STEPS] : LEGACY_IMPORT_STEPS;

  const files = importableFiles(props.files);
  const done = step === 'import';
  const busy = importing || deleting;
  const jobActive = isImportJobActive(job);

  const resetPreview = () => {
    previewRequest.current++;
    setPreview(undefined);
    setPreviewFileId(undefined);
    setPreviewLoading(false);
    setPreviewError(undefined);
    setBaseline(undefined);
    setForm(undefined);
    setApplied(undefined);
    setDeleteError(undefined);
    setDeletedCount(undefined);
    setImportError(undefined);
    setReviewNotice(undefined);
    reviewStarted.current = false;
    staleRegenerations.current = 0;
  };

  useEffect(() => {
    if (props.open) {
      setStep('file');
      setSelectedId(props.initialFileId);
      setResult(undefined);
      setJob(undefined);
      setAlreadyRunning(false);
      finishedJobId.current = undefined;
      setConfirmDelete(false);
      resetPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  // ---- seguimiento del job (cada 3 s mientras el asistente está abierto y el job activo) ----
  useSyncPolling({
    enabled: props.open && !!job && jobActive && !!props.onLoadJob,
    intervalMs: IMPORT_JOB_DRAWER_POLL_MS,
    onTick: async () => {
      if (!job || !props.onLoadJob) return;
      const next = await props.onLoadJob(job.id);
      if (next) setJob((current) => (current && current.id === next.id ? { ...current, ...next } : current));
    },
  });

  // El asistente muestra el job: el aviso global no lleva toast mientras tanto.
  const jobId = job?.id;
  useEffect(() => {
    if (!props.open || !jobId || !jobActive) return undefined;
    props.onJobForeground?.(jobId, true);
    return () => props.onJobForeground?.(jobId, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open, jobId, jobActive]);

  // Fin del job en el asistente: resumen (job.result) o error; el aviso global no lo repite.
  useEffect(() => {
    if (!job || !isImportJobFinished(job) || finishedJobId.current === job.id) return;
    finishedJobId.current = job.id;
    if (isImportJobSucceeded(job)) {
      const summary = importJobResultOf(job);
      setResult(summary);
      props.onImported?.(String(job.providerFileId ?? selectedId ?? ''), summary);
    }
    if (props.open) props.onJobFinished?.(job);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job]);

  const followJob = (next: ProviderImportJob, existing: boolean) => {
    finishedJobId.current = undefined;
    setResult(undefined);
    setAlreadyRunning(existing);
    setJob(next);
    setStep('import');
    props.onJobStarted?.(next);
  };

  const toggleBrowserNotify = async (checked: boolean) => {
    if (!checked) {
      setBrowserNotifyPreference(false);
      setNotifyWanted(false);
      return;
    }
    const permission = await requestBrowserNotificationPermission();
    setNotifyPermission(permission);
    setBrowserNotifyPreference(permission === 'granted');
    setNotifyWanted(permission === 'granted');
  };

  const documentStrategy = isDocumentStrategy(preview?.normalization);
  const overrideOf = (values: ImportMappingForm | undefined): ImportMappingOverride | undefined =>
    values && baseline ? buildMappingOverride(values, baseline, !documentStrategy) : undefined;
  const dirty = !!form && !!applied && !sameMappingForm(form, applied);

  /**
   * Pide la vista previa. `reset`: primera lectura del archivo (sin override) → guarda el plan como base.
   * Si no, `snapshot` es el formulario que se mandó y queda como "aplicado".
   */
  const loadPreview = async (fileId: string, override: ImportMappingOverride | undefined, snapshot?: ImportMappingForm, notice?: ReviewNotice) => {
    const requestId = ++previewRequest.current;
    setPreviewLoading(true);
    setPreviewError(undefined);
    try {
      const data = await props.onPreview(fileId, buildImportRequest(override));
      if (requestId !== previewRequest.current) return;
      // Otra vista previa (otro previewId): la selección anterior se descarta y se avisa (FR-007).
      if (notice) {
        setReviewNotice(notice);
        reviewStarted.current = false;
      }
      setPreview(data);
      setPreviewFileId(fileId);
      if (!snapshot) {
        const initial = mappingFormFrom(data?.normalization, data?.detectedColumns);
        setBaseline(initial);
        setForm(initial);
        setApplied(initial);
      } else {
        setApplied(snapshot);
      }
    } catch (failure: unknown) {
      if (requestId === previewRequest.current) setPreviewError(failure);
    } finally {
      if (requestId === previewRequest.current) setPreviewLoading(false);
    }
  };

  const goToMapping = () => {
    if (!selectedId) return;
    setStep('mapping');
    setImportError(undefined);
    if (previewFileId !== selectedId || !preview) {
      resetPreview();
      loadPreview(selectedId, undefined);
    }
  };

  const recalculate = () => {
    if (!selectedId || !form || !baseline || !isValidCurrencyCode(form.defaultCurrencyCode)) return;
    setDeletedCount(undefined);
    setImportError(undefined);
    staleRegenerations.current = 0;
    loadPreview(selectedId, overrideOf(form), form, reviewStarted.current ? 'discarded' : undefined);
  };

  const retryPreview = () => {
    if (!selectedId) return;
    if (!baseline) loadPreview(selectedId, undefined);
    else loadPreview(selectedId, overrideOf(form), form);
  };

  const deleteImported = async () => {
    setConfirmDelete(false);
    if (!selectedId || !props.onDeleteImported) return;
    setDeleting(true);
    setDeleteError(undefined);
    setDeletedCount(undefined);
    try {
      const response = await props.onDeleteImported(selectedId);
      setDeletedCount(response?.deleted ?? 0);
      setImportError(undefined);
      props.onDeletedImported?.(selectedId, response);
      // Recalcula con el mapeo aplicado: ya no hay importados ni conflicto.
      await loadPreview(selectedId, overrideOf(applied), applied ?? form, reviewStarted.current ? 'discarded' : undefined);
    } catch (failure: unknown) {
      setDeleteError(failure);
    } finally {
      setDeleting(false);
    }
  };

  /** La vista previa venció o se reemplazó (409 PreviewExpired / 404): se vuelve a pedir con el mismo mapeo (FR-007). */
  const regeneratePreview = () => {
    if (!selectedId) return;
    setImportError(undefined);
    loadPreview(selectedId, overrideOf(applied), applied ?? form, 'expired');
  };

  const selection = useImportSelection({
    preview: reviewAvailable ? preview : undefined,
    active: props.open && step === 'review' && reviewAvailable && !previewLoading,
    loadItems: props.onLoadPreviewItems,
    loadItemIds: props.onLoadPreviewItemIds,
    onPreviewGone: () => regeneratePreview(),
    onStale: () => {
      // Otra pestaña reemplazó la vista previa: se regenera una sola vez (si persiste, se usa lo que llegó).
      if (staleRegenerations.current > 0) return;
      staleRegenerations.current++;
      regeneratePreview();
    },
  });

  const assignmentOptions = useAssignmentOptions(props.open && step === 'review' && reviewAvailable, props.onLoadProtocols, props.onLoadPatients);

  // Una vista previa nueva sin previewId (backend anterior) no tiene revisión: vuelve al mapeo.
  useEffect(() => {
    if (step === 'review' && preview && !previewLoading && !reviewAvailable) setStep('mapping');
  }, [step, preview, previewLoading, reviewAvailable]);

  const goToReview = () => {
    if (!reviewAvailable) return;
    reviewStarted.current = true;
    setImportError(undefined);
    setStep('review');
  };

  const doImport = async () => {
    if (!selectedId || !applied) return;
    const withReview = reviewAvailable && !!selection.previewId;
    if (withReview && selection.count === 0) return;
    setImporting(true);
    setImportError(undefined);
    setDeletedCount(undefined);
    if (withReview && selection.state.itemErrors.size > 0) selection.dispatch({ type: 'setItemErrors', itemErrors: [] });
    try {
      const body = withReview ? buildReviewImportRequest(selection.state) : buildImportRequest(overrideOf(applied));
      const response = await props.onImport(selectedId, body);
      if (isImportJob(response)) {
        followJob(response, false);
      } else {
        // backend anterior a MP 030: resumen sincrónico
        setJob(undefined);
        setResult(response);
        setStep('import');
        props.onImported?.(selectedId, response);
      }
      setReviewNotice(undefined);
    } catch (failure: unknown) {
      await onImportFailure(selectedId, failure, withReview);
    } finally {
      setImporting(false);
    }
  };

  /** Error de "Importar": job en curso (409 + jobId), vista previa vencida, conflicto de mapeo o 400 por ítem. */
  const onImportFailure = async (fileId: string, failure: unknown, withReview: boolean) => {
    const runningJobId = conflictJobIdOf(failure);
    if (runningJobId) {
      // 409 con jobId: ya hay una importación en curso de este archivo → se sigue ésa
      let existing = placeholderImportJob(runningJobId, props.providerId, fileId, fileNameOf(fileId));
      try {
        existing = (await props.onLoadJob?.(runningJobId)) ?? existing;
      } catch {
        // se sigue con el provisorio; la próxima consulta trae el estado
      }
      followJob(existing, true);
      return;
    }
    if (withReview && isPreviewGoneError(failure)) {
      setStep('review');
      regeneratePreview();
      return;
    }
    if (withReview && conflictReasonOf(failure) === 'MappingConflict') {
      // 409 de 007: el archivo ya tiene importados con otro mapeo → el paso de mapeo ofrece eliminarlos.
      setImportError(failure);
      setStep('mapping');
      return;
    }
    const itemErrors = (failure as ImportRequestError | undefined)?.ItemErrors;
    if (withReview && itemErrors?.length) selection.dispatch({ type: 'setItemErrors', itemErrors });
    setImportError(failure);
  };

  const linkFromStorage = async (path: string) => {
    if (!props.onLinkFile) return;
    const id = await props.onLinkFile(path);
    if (id) lastLinkedId.current = String(id);
  };

  const onLinkedFromStorage = async () => {
    const id = lastLinkedId.current;
    lastLinkedId.current = undefined;
    await props.onReloadFiles();
    if (id) setSelectedId(id);
  };

  const restart = () => {
    setStep('file');
    setResult(undefined);
    setJob(undefined);
    setAlreadyRunning(false);
    setSelectedId(undefined);
    resetPreview();
  };

  const selectFile = (id: string) => {
    if (busy) return;
    setSelectedId(id);
  };

  const openFilePreview = (file: ProviderFileViewModel) =>
    setFilePreview({ path: filePreviewPath(file), name: fileDisplayName(file), extension: file.fileExtension, size: file.fileSize });

  function fileNameOf(id: string | undefined): string {
    const file = props.files.find((item) => String(item.id) === id);
    return file ? fileDisplayName(file) : '';
  }

  // ---- paso 1 ----
  const renderFiles = () => {
    if (props.filesLoading && files.length === 0) return <GuideSkeleton rows={3} label={tr('loadingFiles')} data-testid="pci-files-loading" />;
    if (props.filesError && files.length === 0) {
      return (
        <GuideMessageBar intent="error" layout="multiline" data-testid="pci-files-error">
          <MessageBarBody>
            <MessageBarTitle>{tr('loadFilesError')}</MessageBarTitle>
            {getApiErrorMessage(props.filesError)}
          </MessageBarBody>
        </GuideMessageBar>
      );
    }
    if (files.length === 0) {
      return <GuideEmptyState title={tr('noFilesTitle')} text={tr('noFilesText')} data-testid="pci-files-empty" />;
    }
    return (
      <GuideTable ariaLabel={tr('filesSection')} data-testid="pci-files-table">
        <thead>
          <tr>
            <th className={cell('check')} scope="col">
              <span className="pci-sr">{tr('selectColumn')}</span>
            </th>
            <GuideTh>{tr('fileColumn')}</GuideTh>
            <GuideTh>{tr('typeColumn')}</GuideTh>
            <GuideTh>{tr('uploadedColumn')}</GuideTh>
            <GuideTh align="end">{tr('sizeColumn')}</GuideTh>
            <th className={cell('actions')} scope="col">
              <span className="pci-sr">{tr('preview')}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {files.map((file) => {
            const id = String(file.id);
            const name = fileDisplayName(file);
            const selected = id === selectedId;
            const invoice = Number(file.fileType) === PROVIDER_FILE_TYPE_INVOICE;
            return (
              <tr key={id} className={guideRowClassName({ selected })} onClick={() => selectFile(id)} data-testid={`pci-file-row-${id}`}>
                <td className={cell('check')}>
                  <input
                    type="radio"
                    className="pci-radio"
                    name="pci-import-file"
                    checked={selected}
                    disabled={busy}
                    onChange={() => selectFile(id)}
                    aria-label={tr('selectFile', { name })}
                  />
                </td>
                <td>
                  <span className="pci-strong">{name}</span>
                </td>
                <td>
                  <StatusPill tone={invoice ? 'neutral' : 'info'}>
                    {invoice ? t('azureFileUpload.fileTypes.providerInvoice') : t('azureFileUpload.fileTypes.providerServicesPerformed')}
                  </StatusPill>
                </td>
                <td className="ichtys-num pci-nowrap">{formatImportDate(file.uploadedAt)}</td>
                <td className={cell('end', 'num')}>{formatFileSize(file.fileSize)}</td>
                <td className={cell('actions')}>
                  <Button
                    appearance="subtle"
                    icon={<Eye20Regular />}
                    aria-label={tr('previewOf', { name })}
                    title={tr('preview')}
                    onClick={(event) => {
                      event.stopPropagation();
                      openFilePreview(file);
                    }}
                    data-testid={`pci-file-preview-${id}`}
                  />
                  {props.onUndoImport && (
                    <GuideTooltip content={t('providerConsumptionImport.undo.action')} relationship="description">
                      <Button
                        appearance="subtle"
                        icon={<ArrowUndo20Regular />}
                        aria-label={t('providerConsumptionImport.undo.actionFile', { name })}
                        disabled={busy}
                        onClick={(event) => {
                          event.stopPropagation();
                          props.onUndoImport?.(file);
                        }}
                        data-testid={`pci-file-undo-${id}`}
                      />
                    </GuideTooltip>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </GuideTable>
    );
  };

  const renderFileStep = () => (
    <GuideDrawerSection
      title={tr('filesSection')}
      description={tr('filesDescription')}
      actions={
        <>
          <Button appearance="subtle" icon={<ArrowClockwise20Regular />} onClick={props.onReloadFiles} disabled={props.filesLoading} data-testid="pci-files-reload">
            {tr('reload')}
          </Button>
          <ViewInFilesLink owner={getProviderStorageOwner(props.providerId)} onNavigate={props.onDismiss} />
        </>
      }
      testId="pci-files-section"
    >
      {props.filesError && files.length > 0 && (
        <GuideMessageBar intent="error" layout="multiline">
          <MessageBarBody>
            <MessageBarTitle>{tr('loadFilesError')}</MessageBarTitle>
            {getApiErrorMessage(props.filesError)}
          </MessageBarBody>
          <MessageBarActions>
            <GuideBannerAction onClick={props.onReloadFiles}>{tr('reload')}</GuideBannerAction>
          </MessageBarActions>
        </GuideMessageBar>
      )}
      {renderFiles()}
    </GuideDrawerSection>
  );

  // ---- paso 2 ----
  const renderImportError = () => {
    if (importError === undefined) return null;
    const conflict = isConflictError(importError);
    return (
      <GuideMessageBar intent={conflict ? 'warning' : 'error'} layout="multiline" data-testid="pci-import-error">
        <MessageBarBody>
          <MessageBarTitle>{conflict ? trm('conflictTitle') : tr('importError')}</MessageBarTitle>
          {getApiErrorMessage(importError)}
        </MessageBarBody>
        {conflict && props.onDeleteImported && (
          <MessageBarActions>
            <Button appearance="secondary" icon={<Delete20Regular />} onClick={() => setConfirmDelete(true)} disabled={busy} data-testid="pci-import-error-delete">
              {trm('deleteImported')}
            </Button>
          </MessageBarActions>
        )}
      </GuideMessageBar>
    );
  };

  const renderReviewNotice = () => {
    if (!reviewNotice) return null;
    return (
      <GuideMessageBar intent={reviewNotice === 'expired' ? 'warning' : 'info'} layout="multiline" data-testid={`pci-review-notice-${reviewNotice}`}>
        <MessageBarBody>
          <MessageBarTitle>{trv(`notices.${reviewNotice}Title`)}</MessageBarTitle>
          {trv(`notices.${reviewNotice}Text`)}
        </MessageBarBody>
      </GuideMessageBar>
    );
  };

  // ---- paso "Revisar ítems" (spec 009) ----
  const renderReviewBody = () => {
    if (preview && !previewLoading) {
      return (
        <ImportItemsReview
          preview={preview}
          selection={selection}
          busy={busy}
          onViewFile={(path, name) => setFilePreview({ path, name, extension: name.includes('.') ? name.split('.').pop() : undefined })}
          assignment={assignmentOptions}
          templates={props.onTestTemplate && props.onCreateTemplate ? { onTest: props.onTestTemplate, onCreate: props.onCreateTemplate } : undefined}
        />
      );
    }
    if (previewError !== undefined && !previewLoading) {
      return (
        <GuideMessageBar intent="error" layout="multiline" data-testid="pci-preview-error">
          <MessageBarBody>
            <MessageBarTitle>{trm('error')}</MessageBarTitle>
            {getApiErrorMessage(previewError)}
          </MessageBarBody>
          <MessageBarActions>
            <GuideBannerAction onClick={retryPreview}>{trm('retry')}</GuideBannerAction>
          </MessageBarActions>
        </GuideMessageBar>
      );
    }
    return <GuideSkeleton rows={6} label={trm('loading')} data-testid="pci-review-preview-loading" />;
  };

  const renderReviewStep = () => (
    <>
      {renderReviewNotice()}
      {renderImportError()}
      {renderReviewBody()}
    </>
  );

  const renderMappingStep = () => (
    <>
      {renderReviewNotice()}
      {renderImportError()}
      {form && (
        <ImportMappingPreview
          preview={preview}
          loading={previewLoading}
          error={previewError}
          form={form}
          onFormChange={setForm}
          dirty={dirty}
          busy={busy}
          onRecalculate={recalculate}
          onRetry={retryPreview}
          onRequestDelete={props.onDeleteImported ? () => setConfirmDelete(true) : undefined}
          deleting={deleting}
          deletedCount={deletedCount}
          deleteError={deleteError}
        />
      )}
      {!form && previewLoading && <GuideSkeleton rows={6} label={trm('loading')} data-testid="pci-preview-loading" />}
      {!form && !previewLoading && previewError !== undefined && (
        <GuideMessageBar intent="error" layout="multiline" data-testid="pci-preview-error">
          <MessageBarBody>
            <MessageBarTitle>{trm('error')}</MessageBarTitle>
            {getApiErrorMessage(previewError)}
          </MessageBarBody>
          <MessageBarActions>
            <GuideBannerAction onClick={retryPreview}>{trm('retry')}</GuideBannerAction>
          </MessageBarActions>
        </GuideMessageBar>
      )}
    </>
  );

  // ---- paso 3 ----
  const renderResult = () => {
    const warnings = result?.warnings ?? [];
    const listed = warnings.slice(0, MAX_LISTED_WARNINGS);
    const assignmentWarnings = result?.assignmentWarnings ?? [];
    return (
      <div className="pci-stack" data-testid="pci-import-result">
        <GuideMessageBar intent="success" layout="multiline">
          <MessageBarBody>
            <MessageBarTitle>{tr('resultTitle')}</MessageBarTitle>
            {tr('resultText', { name: fileNameOf(selectedId) || job?.fileName || '' })}
          </MessageBarBody>
        </GuideMessageBar>
        <GuideKpiGrid>
          <GuideKpiCard label={tr('result.totalRows')} value={result?.totalRows ?? 0} valueTestId="pci-result-total" />
          <GuideKpiCard label={tr('result.created')} value={result?.created ?? 0} tone="success" valueTestId="pci-result-created" />
          <GuideKpiCard label={tr('result.skippedExisting')} value={result?.skippedExisting ?? 0} valueTestId="pci-result-skipped" />
          <GuideKpiCard label={tr('result.medicalServicesCreated')} value={result?.medicalServicesCreated ?? 0} tone="info" />
          <GuideKpiCard label={tr('result.medicalServicesReused')} value={result?.medicalServicesReused ?? 0} />
          {result?.medicalServicesMatchedByName !== undefined && (
            <GuideKpiCard
              label={tr('result.medicalServicesMatchedByName')}
              value={result.medicalServicesMatchedByName}
              tone={result.medicalServicesMatchedByName > 0 ? 'warning' : undefined}
              valueTestId="pci-result-matched-by-name"
            />
          )}
          {result?.valuedByFileValue !== undefined && (
            <GuideKpiCard label={tr('result.valuedByFileValue')} value={result.valuedByFileValue} tone="success" valueTestId="pci-result-valued-file" />
          )}
          {result?.valuedByPriceList !== undefined && (
            <GuideKpiCard label={tr('result.valuedByPriceList')} value={result.valuedByPriceList} tone="info" valueTestId="pci-result-valued-price-list" />
          )}
          {result?.unvalued !== undefined && (
            <GuideKpiCard
              label={tr('result.unvalued')}
              value={result.unvalued}
              tone={result.unvalued > 0 ? 'warning' : undefined}
              detail={result.unvalued > 0 ? tr('result.unvaluedDetail') : undefined}
              valueTestId="pci-result-unvalued"
            />
          )}
          {result?.selected !== undefined && result?.selected !== null && (
            <GuideKpiCard label={tr('result.selected')} value={result.selected} tone="info" valueTestId="pci-result-selected" />
          )}
          {result?.excludedByUser !== undefined && result?.excludedByUser !== null && (
            <GuideKpiCard label={tr('result.excludedByUser')} value={result.excludedByUser} valueTestId="pci-result-excluded-user" />
          )}
          {result?.assigned !== undefined && result?.assigned !== null && (
            <GuideKpiCard label={tr('result.assigned')} value={result.assigned} tone="success" valueTestId="pci-result-assigned" />
          )}
          {result?.servicesChosen !== undefined && result?.servicesChosen !== null && (
            <GuideKpiCard label={tr('result.servicesChosen')} value={result.servicesChosen} tone="info" valueTestId="pci-result-services-chosen" />
          )}
          {result?.servicesCreatedOnRequest !== undefined && result?.servicesCreatedOnRequest !== null && (
            <GuideKpiCard
              label={tr('result.servicesCreatedOnRequest')}
              value={result.servicesCreatedOnRequest}
              tone="info"
              valueTestId="pci-result-services-created-on-request"
            />
          )}
          {result?.mappingsSaved !== undefined && result?.mappingsSaved !== null && (
            <GuideKpiCard
              label={tr('result.mappingsSaved')}
              value={result.mappingsSaved}
              tone="success"
              detail={tr('result.mappingsSavedDetail')}
              valueTestId="pci-result-mappings-saved"
            />
          )}
          {result?.excludedByStatus && (
            <GuideKpiCard
              label={tr('result.excludedByStatus')}
              value={excludedByStatusTotal(result.excludedByStatus)}
              detail={excludedByStatusDetail(result.excludedByStatus, (key) => tr(`result.excludedKinds.${key}`)) || undefined}
              valueTestId="pci-result-excluded-status"
              data-testid="pci-result-excluded-status-card"
            />
          )}
        </GuideKpiGrid>
        {assignmentWarnings.length > 0 && (
          <GuideMessageBar intent="warning" layout="multiline" data-testid="pci-import-assignment-warnings">
            <MessageBarBody>
              <MessageBarTitle>{tr('assignmentWarningsTitle', { count: assignmentWarnings.length })}</MessageBarTitle>
              <ul className="pci-list">
                {assignmentWarnings.slice(0, MAX_LISTED_WARNINGS).map((warning, index) => (
                  <li key={index}>{warning}</li>
                ))}
              </ul>
              {assignmentWarnings.length > MAX_LISTED_WARNINGS && (
                <span className="pci-secondary">{tr('moreWarnings', { count: assignmentWarnings.length - MAX_LISTED_WARNINGS })}</span>
              )}
            </MessageBarBody>
          </GuideMessageBar>
        )}
        {warnings.length > 0 && (
          <GuideMessageBar intent="warning" layout="multiline" data-testid="pci-import-warnings">
            <MessageBarBody>
              <MessageBarTitle>{tr('warningsTitle', { count: warnings.length })}</MessageBarTitle>
              <ul className="pci-list">
                {listed.map((warning, index) => (
                  <li key={index}>{warning}</li>
                ))}
              </ul>
              {warnings.length > listed.length && <span className="pci-secondary">{tr('moreWarnings', { count: warnings.length - listed.length })}</span>}
            </MessageBarBody>
          </GuideMessageBar>
        )}
      </div>
    );
  };

  // ---- paso 3 en segundo plano (spec 008) ----
  const renderJob = () => {
    if (!job) return null;
    const name = fileNameOf(selectedId) || job.fileName || '';
    return (
      <div className="pci-stack" data-testid="pci-import-job">
        {alreadyRunning && (
          <GuideMessageBar intent="info" layout="multiline" data-testid="pci-job-already-running">
            <MessageBarBody>
              <MessageBarTitle>{trj('alreadyRunningTitle')}</MessageBarTitle>
              {trj('alreadyRunningText')}
            </MessageBarBody>
          </GuideMessageBar>
        )}
        {jobActive && (
          <GuideDrawerSection title={trj('inProgressTitle')} description={trj('inProgressDescription', { name })} testId="pci-job-section">
            <ImportJobProgressBar
              job={job}
              detail={job.requestedAt ? trj('requestedAt', { date: formatDateTime(job.requestedAt) }) : undefined}
              data-testid="pci-job-progress"
            />
            <GuideMessageBar intent="info" layout="multiline" data-testid="pci-job-background">
              <MessageBarBody>
                <MessageBarTitle>{trj('backgroundTitle')}</MessageBarTitle>
                {trj('backgroundText')}
              </MessageBarBody>
            </GuideMessageBar>
            {notifyPermission !== 'unsupported' && (
              <div className="pci-notify">
                <Checkbox
                  checked={notifyWanted}
                  disabled={notifyPermission === 'denied'}
                  onChange={(_event, data) => toggleBrowserNotify(!!data.checked)}
                  label={trj('browserNotify')}
                  data-testid="pci-job-notify"
                />
                {notifyPermission === 'denied' && <span className="pci-secondary">{trj('browserNotifyDenied')}</span>}
              </div>
            )}
          </GuideDrawerSection>
        )}
        {isImportJobFailed(job) && (
          <GuideMessageBar intent="error" layout="multiline" data-testid="pci-job-failed">
            <MessageBarBody>
              <MessageBarTitle>{trj('failedTitle', { name })}</MessageBarTitle>
              {importJobErrorText(job) || trj('failedText')}
            </MessageBarBody>
          </GuideMessageBar>
        )}
      </div>
    );
  };

  // ---- pie ----
  const mappingBlocked =
    !preview ||
    !applied ||
    previewLoading ||
    dirty ||
    !!preview.mappingConflict ||
    (form ? !isValidCurrencyCode(form.defaultCurrencyCode) : true);

  let extraActions: GuideDrawerAction[] | undefined;
  let primaryAction: GuideDrawerAction | undefined;
  if (step === 'file') {
    extraActions = props.onLinkFile
      ? [{ label: tr('linkFromStorage'), icon: <Link20Regular />, onClick: () => setPickerOpen(true), testId: 'pci-link-from-storage' }]
      : undefined;
    primaryAction = { label: tr('next'), onClick: goToMapping, disabled: !selectedId, testId: 'pci-next' };
  } else if (step === 'mapping') {
    extraActions = [{ label: tr('back'), icon: <ArrowLeft20Regular />, onClick: () => setStep('file'), disabled: busy, testId: 'pci-back' }];
    primaryAction = reviewAvailable
      ? { label: tr('nextReview'), onClick: goToReview, disabled: mappingBlocked || deleting, testId: 'pci-next-review' }
      : {
          label: importing ? tr('importing') : tr('import'),
          onClick: doImport,
          disabled: mappingBlocked || deleting,
          loading: importing,
          testId: 'pci-import-submit',
        };
  } else if (step === 'review') {
    const count = selection.count;
    const importLabel = count === 1 ? trv('importOne') : trv('importCount', { count });
    extraActions = [{ label: tr('back'), icon: <ArrowLeft20Regular />, onClick: () => setStep('mapping'), disabled: busy, testId: 'pci-back' }];
    primaryAction = {
      label: importing ? tr('importing') : importLabel,
      onClick: doImport,
      disabled: count === 0 || !selection.ready || previewLoading || selection.selectingAll || deleting,
      loading: importing,
      testId: 'pci-import-submit',
    };
  } else if (jobActive) {
    // en curso: sólo "Cerrar" (se puede seguir trabajando; el aviso global avisa al terminar)
    extraActions = undefined;
  } else if (job && isImportJobFailed(job)) {
    extraActions = [{ label: tr('importAnother'), onClick: restart, testId: 'pci-import-another' }];
    primaryAction = applied
      ? { label: importing ? tr('importing') : trj('retry'), onClick: doImport, loading: importing, testId: 'pci-job-retry' }
      : undefined;
  } else {
    extraActions = [{ label: tr('importAnother'), onClick: restart, testId: 'pci-import-another' }];
  }

  const stepperSteps = steps.map((key) => ({ key, label: tr(`steps.${key}`) }));
  const currentIndex = Math.max(0, steps.indexOf(step));

  return (
    <>
      <GuideDrawer
        open={props.open}
        size={step === 'review' ? 'wide' : 'large'}
        onDismiss={props.onDismiss}
        preventDismiss={busy}
        closeLabel={tr('close')}
        eyebrow={tr('eyebrow', { name: props.providerName })}
        title={tr('title')}
        subtitle={step !== 'file' && selectedId ? fileNameOf(selectedId) : undefined}
        testId="pci-import-drawer"
        secondaryAction={{ label: done ? tr('close') : tr('cancel') }}
        extraActions={extraActions}
        primaryAction={primaryAction}
      >
        <GuideStepper
          steps={stepperSteps}
          current={currentIndex}
          ariaLabel={tr('stepsLabel')}
          doneLabel={tr('stepDone')}
          disabled={busy || done}
          onStepClick={(index) => setStep(steps[index] ?? 'file')}
          data-testid="pci-stepper"
        />
        {step === 'file' && renderFileStep()}
        {step === 'mapping' && renderMappingStep()}
        {step === 'review' && renderReviewStep()}
        {step === 'import' && job && !isImportJobSucceeded(job) && renderJob()}
        {step === 'import' && (!job || isImportJobSucceeded(job)) && renderResult()}
      </GuideDrawer>
      <Dialog open={confirmDelete} modalType="alert" onOpenChange={(_event, data) => !data.open && setConfirmDelete(false)}>
        <DialogSurface data-testid="pci-delete-dialog">
          <DialogBody>
            <DialogTitle>{trm('deleteConfirmTitle')}</DialogTitle>
            <DialogContent>
              <p>{trm('deleteConfirmText', { name: fileNameOf(selectedId), count: Number(preview?.alreadyImportedCount ?? 0) })}</p>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setConfirmDelete(false)} data-testid="pci-delete-cancel">
                {trm('deleteCancel')}
              </Button>
              <Button appearance="secondary" className="pci-danger" icon={<Delete20Regular />} onClick={deleteImported} data-testid="pci-delete-confirm">
                {trm('deleteConfirm')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
      {filePreview && <FilePreview open={!!filePreview} file={filePreview} onDismiss={() => setFilePreview(undefined)} />}
      {pickerOpen && props.onLinkFile && (
        <FilePickerDialog
          open={pickerOpen}
          onDismiss={() => setPickerOpen(false)}
          title={tr('linkPickerTitle', { name: props.providerName })}
          initialPath={getOwnerFolderPath(getProviderStorageOwner(props.providerId)) ?? ''}
          allowedExtensions={IMPORTABLE_FILE_EXTENSIONS}
          linkFile={linkFromStorage}
          onLinked={onLinkedFromStorage}
        />
      )}
    </>
  );
};

export default ImportConsumptionsDrawer;
